// New tagged slice for the migrated `_features/usageSources` feature -
// consolidates TWO duplicate injectEndpoints calls that both existed on
// the same apiSlice for /usagesource routes (the old `features/api/
// usageSourceApiSlice.js`, since removed entirely, and
// features/UsageSourcesFeature/api/usageSourcesApi.js). Unlike the
// equivalent Billing Units collision, this one was benign - both
// injections build their 6 shared endpoints from
// the same apiRoutes.usageSource.* table, so the definitions were
// byte-identical (verified before writing this file, not assumed) - no
// live 404, just a harmless RTK Query dev "endpoint already exists"
// warning. Consolidated anyway per this migration's standard, and given
// real cache tags neither original had (both had zero providesTags/
// invalidatesTags, so the UI relied entirely on manual refetch() calls).
//
// useGetAllUsageSourceQuery -> GET /usagesource (the *filtered/paginated*
// list, used by the list page) - matches every sibling feature's own
// convention for its base-route endpoint (Markets' useGetAllMarketQuery,
// Products' useGetAllProductQuery, Plans' useGetAllPlanQuery all name their
// paginated/filtered endpoint this same way).
// useGetAllUsageSourceByOrgIdQuery -> GET /usagesource/all (the *full*,
// unfiltered list, used as the shared picker by useResolveFormSchema.js +
// Usage Compilation's form) - was `useGetUsageSourceQuery`, which read as
// the OPPOSITE of what it actually returns next to its sibling above.
// Renamed to match the same `...ByOrgId` qualifier every sibling feature's
// full-list/picker endpoint already uses (getAllMarketsByOrgId,
// getAllProductByOrgId, getAllPlanByOrgId) - this was the one genuinely
// inverted name, not a one-off rename.
//
// Drops deleteUsageSource (apiRoutes.usageSource.delete is a real backend
// route, confirmed zero UI consumers - archive/activate via
// updateUsageSourceStatus is what the page actually uses, same as every
// prior migration's dead delete endpoint) and the fat slice's
// updateUsageSourceStatus (also zero consumers - the page always used the
// lean slice's differently-named changeUsageSourceStatus for the same
// apiRoutes.usageSource.updateStatus path/method).
import { apiRoutes } from "../../../_config/apiRoutes";
import { apiSlice } from "../../../_api/apiSlice";

export const usageSourceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllUsageSource: builder.query({
      query: (params) => ({
        url: apiRoutes.usageSource.get.path,
        method: apiRoutes.usageSource.get.reqType,
        params,
      }),
      providesTags: ["UsageSources"],
    }),
    getAllUsageSourceByOrgId: builder.query({
      query: (params) => ({
        url: apiRoutes.usageSource.getAll.path,
        method: apiRoutes.usageSource.getAll.reqType,
        params,
      }),
      providesTags: ["UsageSources"],
    }),
    createUsageSource: builder.mutation({
      query: (data) => ({
        url: apiRoutes.usageSource.create.path,
        method: apiRoutes.usageSource.create.reqType,
        body: data,
      }),
      invalidatesTags: ["UsageSources"],
    }),
    editUsageSource: builder.mutation({
      query: (data) => ({
        url: apiRoutes.usageSource.edit.path,
        method: apiRoutes.usageSource.edit.reqType,
        body: data,
      }),
      invalidatesTags: ["UsageSources"],
    }),
    changeUsageSourceStatus: builder.mutation({
      query: (data) => ({
        url: apiRoutes.usageSource.updateStatus.path,
        method: apiRoutes.usageSource.updateStatus.reqType,
        body: data,
      }),
      invalidatesTags: ["UsageSources"],
    }),
    validateUsageSource: builder.mutation({
      query: (data) => ({
        url: apiRoutes.usageSource.validate.path,
        method: apiRoutes.usageSource.validate.reqType,
        body: data,
      }),
    }),
  }),
});

export const {
  useGetAllUsageSourceQuery,
  useGetAllUsageSourceByOrgIdQuery,
  useCreateUsageSourceMutation,
  useEditUsageSourceMutation,
  useChangeUsageSourceStatusMutation,
  useValidateUsageSourceMutation,
} = usageSourceApi;
